import {
  Button,
  Checkbox,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  athleteDisplayName,
  AthletePreview,
  WorkoutStatus,
} from '@strength/domain';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Drawer, DrawerFooter, DrawerHeader } from '../../components/drawer';
import { AthleteWorkoutStatus } from './types';

type OnSaveProps = {
  plannedAthleteIds: string[];
  completedAthleteIds: string[];
  skippedAthleteIds: string[];
};

export const CompleteWorkoutHeader = ({
  workoutName,
  athletes,
  athleteWorkouts,
  onSaveWorkoutStatuses,
}: {
  workoutId: string;
  workoutName: string;
  athletes: AthletePreview[];
  athleteWorkouts: {
    workoutId: string;
    profileId: string;
    status: WorkoutStatus;
  }[];
  onSaveWorkoutStatuses: (
    statuses: {
      profileId: string;
      status: WorkoutStatus;
    }[],
  ) => void;
}) => {
  const { t } = useTranslation();
  const [completeDrawerOpen, setCompleteDrawerOpen] = useState(false);

  const onSave = ({
    plannedAthleteIds,
    completedAthleteIds,
    skippedAthleteIds,
  }: OnSaveProps) => {
    onSaveWorkoutStatuses([
      ...plannedAthleteIds.map((p) => ({
        profileId: p,
        status: 'planned' as const,
      })),
      ...completedAthleteIds.map((p) => ({
        profileId: p,
        status: 'completed' as const,
      })),
      ...skippedAthleteIds.map((p) => ({
        profileId: p,
        status: 'skipped' as const,
      })),
    ]);
    setCompleteDrawerOpen(false);
  };

  return (
    <>
      <Button onClick={() => setCompleteDrawerOpen(true)}>
        {t('editStatus')}
      </Button>
      {completeDrawerOpen && (
        <CompleteWorkoutDrawer
          workoutName={workoutName}
          open={completeDrawerOpen}
          onOpenChange={setCompleteDrawerOpen}
          athletes={athletes}
          athleteWorkoutStatuses={athleteWorkouts}
          onSave={onSave}
        />
      )}
    </>
  );
};

const CompleteWorkoutDrawer = ({
  workoutName,
  athletes,
  athleteWorkoutStatuses,
  open,
  onOpenChange,
  onSave,
}: {
  workoutName: string;
  athletes: AthletePreview[];
  athleteWorkoutStatuses: AthleteWorkoutStatus[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (props: OnSaveProps) => void;
}) => {
  const { t } = useTranslation();
  const [toggle, setToggle] = useState<'planned' | 'completed' | 'skipped'>(
    'planned',
  );
  const athleteIdsByStatus = useMemo(() => {
    const map = new Map<WorkoutStatus, string[]>();
    athleteWorkoutStatuses.forEach((ws) => {
      const statusArray = map.get(ws.status) ?? [];
      map.set(ws.status, [...statusArray, ws.profileId]);
    });
    return map;
  }, [athleteWorkoutStatuses]);

  const [selectedAthleteIds, setSelectedAthleteIds] = useState<string[]>([]);
  const plannedAthleteIds = athleteIdsByStatus.get('planned') ?? [];
  const completedAthleteIds = athleteIdsByStatus.get('completed') ?? [];
  const skippedAthleteIds = athleteIdsByStatus.get('skipped') ?? [];

  const selectableWorkoutsForView =
    toggle === 'planned'
      ? plannedAthleteIds
      : toggle === 'completed'
        ? completedAthleteIds
        : skippedAthleteIds;
  const athletesForView = athletes.filter((a) =>
    selectableWorkoutsForView.find((id) => id === a.id),
  );

  const allSelected =
    athletesForView.length > 0 &&
    selectedAthleteIds.length === athletesForView.length;

  const onSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedAthleteIds(athletesForView.map((a) => a.id));
      return;
    }
    setSelectedAthleteIds([]);
  };

  const onClear = () => {
    setSelectedAthleteIds([]);
  };

  const closeHandler = () => {
    onOpenChange(false);
  };

  const swapWorkoutStatus = ({
    fromStatus,
    toStatus,
  }: {
    fromStatus: WorkoutStatus;
    toStatus: WorkoutStatus;
  }) => {
    const fromIds = athleteIdsByStatus.get(fromStatus) ?? [];
    const toIds = athleteIdsByStatus.get(toStatus) ?? [];
    athleteIdsByStatus.set(toStatus, [...toIds, ...selectedAthleteIds]);
    athleteIdsByStatus.set(
      fromStatus,
      fromIds.filter((id) => !selectedAthleteIds.includes(id)),
    );
    onClear();
  };

  return (
    <Drawer open={open} onClose={closeHandler}>
      <DrawerHeader
        title={t('completeWorkout')}
        onClose={closeHandler}
        className="pb-1"
      />
      <Typography variant="subtitle1" component="p" className="mx-6 mb-8">
        {workoutName}
      </Typography>
      <div className="flex flex-col gap-2 mx-6 overflow-y-hidden grow">
        <ToggleButtonGroup
          exclusive
          fullWidth
          value={toggle}
          onChange={(_, value) => {
            if (value === '') {
              return;
            }
            setToggle(value as typeof toggle);
            onClear();
          }}
        >
          <ToggleButton value="planned" aria-label="planned">
            {t('planned')}
            {plannedAthleteIds.length > 0
              ? ` (${plannedAthleteIds.length})`
              : ''}
          </ToggleButton>
          <ToggleButton value="completed" aria-label="completed">
            {t('completed')}
            {completedAthleteIds.length > 0
              ? ` (${completedAthleteIds.length})`
              : ''}
          </ToggleButton>
          <ToggleButton value="skipped" aria-label="skipped">
            {t('skipped')}
            {skippedAthleteIds.length > 0
              ? ` (${skippedAthleteIds.length})`
              : ''}
          </ToggleButton>
        </ToggleButtonGroup>
        <div className="border border-1 rounded-md p-1">
          <Typography
            variant="body2Medium"
            className="flex items-center font-bold"
          >
            <Checkbox
              size="small"
              onChange={(e) => onSelectAll(e)}
              checked={allSelected}
              inputProps={{
                'aria-label': allSelected ? t('deselectAll') : t('selectAll'),
              }}
            />
            {t('selectAll')}
          </Typography>
          <Divider className="mx-1 mb-1" />
          <div className="flex flex-col overflow-y-scroll max-h-[calc(100vh-420px)] [&::-webkit-scrollbar]:hidden">
            {athletesForView.map((athlete) => {
              return (
                <div key={athlete.id} className="flex items-center">
                  <Checkbox
                    size="small"
                    checked={selectedAthleteIds.includes(athlete.id)}
                    inputProps={{
                      'aria-label': selectedAthleteIds.includes(athlete.id)
                        ? t('unselectAthlete', {
                            name: athleteDisplayName(athlete),
                          })
                        : t('selectAthlete', {
                            name: athleteDisplayName(athlete),
                          }),
                    }}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedAthleteIds([
                          ...selectedAthleteIds,
                          athlete.id,
                        ]);
                      } else {
                        setSelectedAthleteIds(
                          selectedAthleteIds.filter((id) => id !== athlete.id),
                        );
                      }
                    }}
                  />
                  <Typography variant="body2Medium">
                    {athleteDisplayName(athlete)}
                  </Typography>
                </div>
              );
            })}
            {athletesForView.length === 0 && (
              <Typography variant="body2" className="m-2">
                {t('noAthletes')}
              </Typography>
            )}
          </div>
          <Divider className="mx-1 my-2" />
          <div className="flex justify-between items-center">
            <Button
              variant="text"
              size="small"
              className="text-blue-500"
              onClick={onClear}
            >
              {t('clear')}
            </Button>
            <Typography variant="subtitle1" className="mr-2">
              {selectedAthleteIds.length} of{' '}
              {selectableWorkoutsForView.length ?? 0}
            </Typography>
          </div>
        </div>
        <div className="flex flex-row justify-end gap-2">
          {toggle === 'planned' ? (
            <>
              <Button
                variant="text"
                onClick={() =>
                  swapWorkoutStatus({
                    fromStatus: 'planned',
                    toStatus: 'skipped',
                  })
                }
              >
                {t('skip')}
              </Button>
              <Button
                variant="outlined"
                onClick={() =>
                  swapWorkoutStatus({
                    fromStatus: 'planned',
                    toStatus: 'completed',
                  })
                }
              >
                {t('complete')}
              </Button>
            </>
          ) : toggle === 'completed' ? (
            <>
              <Button
                variant="text"
                onClick={() =>
                  swapWorkoutStatus({
                    fromStatus: 'completed',
                    toStatus: 'planned',
                  })
                }
              >
                {t('markPlanned')}
              </Button>
              <Button
                variant="outlined"
                onClick={() =>
                  swapWorkoutStatus({
                    fromStatus: 'completed',
                    toStatus: 'skipped',
                  })
                }
              >
                {t('markSkipped')}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="text"
                onClick={() =>
                  swapWorkoutStatus({
                    fromStatus: 'skipped',
                    toStatus: 'planned',
                  })
                }
              >
                {t('markPlanned')}
              </Button>
              <Button
                variant="outlined"
                onClick={() =>
                  swapWorkoutStatus({
                    fromStatus: 'skipped',
                    toStatus: 'completed',
                  })
                }
              >
                {t('markCompleted')}
              </Button>
            </>
          )}
        </div>
      </div>
      <DrawerFooter className="flex flex-row gap-2 w-full p-8 mt-4 justify-end">
        <Button onClick={() => onOpenChange(false)} variant="outlined">
          {t('cancel')}
        </Button>
        <Button
          disabled={false}
          onClick={() => {
            onSave({
              plannedAthleteIds,
              completedAthleteIds,
              skippedAthleteIds,
            });
          }}
          variant="contained"
        >
          {t('save')}
        </Button>
      </DrawerFooter>
    </Drawer>
  );
};
